import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { feedbackApi, type FeedbackItem, type FeedbackReplyResult } from '@/lib/apiClient';
import { MINE_KEY, fmtWhen } from '@/lib/feedbackMine';
import { boardReplyNote } from './feedbackBoard';

/** What the API accepts for a reply or a closing note: the same cap as the message itself. */
const MAX = 4000;

const MUTED: React.CSSProperties = { margin: 0, fontSize: 12, color: 'var(--ew-n-500)', lineHeight: 1.4 };
const BOX: React.CSSProperties = {
  width: '100%', boxSizing: 'border-box', padding: '8px 10px', border: '1px solid var(--ew-n-200)', borderRadius: 'var(--ew-r-md)',
  font: 'var(--ew-fw-regular) 13px/1.4 var(--ew-font)', resize: 'vertical',
};

/** One plain sentence for what happened to a reply's copy on GitHub. */
export function describeReply(r: FeedbackReplyResult): { tone: 'ok' | 'warn'; text: string } {
  if (r.githubMirrored === false) return { tone: 'warn', text: 'Reply saved. It could not be copied to the GitHub issue.' };
  if (r.githubMirrored === true) return { tone: 'ok', text: 'Reply saved and copied to the GitHub issue.' };
  return { tone: 'ok', text: 'Reply saved.' };
}

/**
 * The conversation on one feedback item, for the Admin tab: what has been said to the people following it, a box to say more, and,
 * for an open item, a way to close it that tells them why.
 *
 * ⚠️ A reply is stored first and everything after it (the GitHub comment, the email) is best-effort, so the answer says which of
 * those happened rather than a bare "sent": an admin who is told "saved" and sees "not copied to GitHub" knows what to do about it.
 *
 * ⚠️ NO `followerCount` (`null`, or absent from an older response) MEANS THE CONVERSATION COULD NOT BE READ, which is not the same as
 * nobody. The list is shown without replies when that happens (the migration has not been run on this database), so this panel says
 * so and does not offer a reply, which could not be stored either.
 */
export default function FeedbackThread({ item }: { item: FeedbackItem }) {
  const qc = useQueryClient();
  const [reply, setReply] = useState('');
  const [note, setNote] = useState('');
  const replies = item.replies ?? [];
  const unavailable = item.followerCount == null;
  const followers = item.followerCount ?? 0;
  // What the board adds to who reads a reply: everyone signed in once the item is public, or the same if it is shown later.
  const replyBoardNote = boardReplyNote(item.boardVisibility, 'reply');
  const noteBoardNote = boardReplyNote(item.boardVisibility, 'note');

  const refresh = () => {
    void qc.invalidateQueries({ queryKey: ['admin', 'feedback'] });
    // An admin who also follows an item sees their own thread under My feedback.
    void qc.invalidateQueries({ queryKey: MINE_KEY });
  };

  const send = useMutation({
    mutationFn: () => feedbackApi.reply(item.id, reply.trim()),
    onSuccess: () => { setReply(''); refresh(); },
  });
  const close = useMutation({
    // No note while the conversation is unavailable, whatever is in the box: the API applies the status first and stores a note
    // best-effort, so with the tables missing it would close the item and silently drop the text. (Codex, #672.)
    mutationFn: (reason: 'completed' | 'not_planned') => feedbackApi.updateStatus(item.id, 'closed', { reason, note: unavailable ? '' : note }),
    onSuccess: () => { setNote(''); refresh(); },
  });

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12, padding: '6px 4px', maxWidth: 720 }}>
      <p style={MUTED}>
        {unavailable
          ? 'The replies could not be loaded for this list, so they are not shown and cannot be added.'
          : followers === 0
            ? 'Nobody is following this item, so nobody is told about replies.'
            : `${followers} ${followers === 1 ? 'person follows' : 'people follow'} this item.`}
      </p>

      {replies.map((r) => (
        <div key={r.id} style={{ padding: '8px 10px', borderLeft: '3px solid var(--ew-green)', background: '#fff', borderRadius: 'var(--ew-r-sm)' }}>
          <div style={{ ...MUTED, marginBottom: 4 }}><strong style={{ color: 'var(--ew-dark)' }}>{r.authorName ?? 'Unknown'}</strong> · {fmtWhen(r.createdAt)}</div>
          <div style={{ whiteSpace: 'pre-wrap', fontSize: 13, lineHeight: 1.45 }}>{r.body}</div>
        </div>
      ))}
      {!unavailable && replies.length === 0 && <p style={MUTED}>No replies yet.</p>}

      {!unavailable && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          <label htmlFor={`fb-reply-${item.id}`} style={{ fontWeight: 600, fontSize: 12 }}>Reply</label>
          <textarea
            id={`fb-reply-${item.id}`}
            style={BOX}
            rows={3}
            maxLength={MAX}
            value={reply}
            onChange={(e) => setReply(e.target.value)}
            placeholder="Say something to the people following this item."
          />
          <p style={MUTED}>
            Shown to everyone following this item under <strong>My feedback</strong>, and copied to its GitHub issue as a comment.
            {replyBoardNote && <> <strong>{replyBoardNote}</strong></>}
          </p>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            <button className="btn btn-primary" style={{ padding: '4px 12px' }} disabled={!reply.trim() || send.isPending} onClick={() => send.mutate()}>
              {send.isPending ? 'Sending…' : 'Send reply'}
            </button>
            {send.isSuccess && (
              <span role="status" style={{ fontSize: 12, color: describeReply(send.data).tone === 'warn' ? 'var(--ew-warning)' : 'var(--ew-n-600)' }}>
                {describeReply(send.data).text}
              </span>
            )}
            {send.isError && <span role="alert" style={{ fontSize: 12, color: 'var(--ew-danger)' }}>{(send.error as Error).message}</span>}
          </div>
        </div>
      )}

      {item.status === 'open' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6, paddingTop: 8, borderTop: '1px solid var(--ew-n-200)' }}>
          {/* ⚠️ NO NOTE BOX WHILE THE CONVERSATION IS UNAVAILABLE, and no promise that one would be delivered: a note is stored as a reply, so
              with the tables missing the item would close and the text would be lost with nothing to say so. Closing itself still works. (Codex, #672.) */}
          {unavailable ? (
            <p style={MUTED}>Closing also closes the GitHub issue. A note cannot be added while replies are unavailable.</p>
          ) : (
            <>
              <label htmlFor={`fb-note-${item.id}`} style={{ fontWeight: 600, fontSize: 12 }}>Close with a note (optional)</label>
              <textarea
                id={`fb-note-${item.id}`}
                style={BOX}
                rows={2}
                maxLength={MAX}
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder="Why, or what changed."
              />
              <p style={MUTED}>
                The note goes to the people following this item, with the message that tells them it is closed. Closing also closes the GitHub issue.
                {noteBoardNote && <> <strong>{noteBoardNote}</strong></>}
              </p>
            </>
          )}
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            <button className="btn btn-primary" style={{ padding: '4px 12px' }} disabled={close.isPending} onClick={() => close.mutate('completed')}>Done</button>
            <button className="btn btn-secondary" style={{ padding: '4px 12px' }} disabled={close.isPending} onClick={() => close.mutate('not_planned')}>Won’t do</button>
            {close.isError && <span role="alert" style={{ fontSize: 12, color: 'var(--ew-danger)' }}>{(close.error as Error).message}</span>}
          </div>
        </div>
      )}
    </div>
  );
}
